import isString from 'lodash/isString';
import merge from 'lodash/merge';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import BlockIcon from '@material-ui/icons/Block';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import EditIcon from '@material-ui/icons/Edit';
import FileCopyIcon from '@material-ui/icons/FileCopy';

import { FormGrid, FormLegend, ModalDialog, RiskDataAutocompleteAsyncValue, Tooltip } from 'components';
import {
  AddRiskColumn,
  AddRiskFormField,
  AddRiskObject,
  AddRiskObjectAddress,
  AddRiskRow,
  AddRiskRowMultiple,
  AddRiskRowMultipleObject,
} from 'modules';

import * as utils from 'utils';

import { RISK_STATUS_BOUND } from 'consts';
import { RiskDetailsContext } from 'pages/RiskDetails/RiskDetails';
import { HideFromUserWrapper, ValueSet } from './components';
import {RiskContext} from "../../forms";

const useStyles = makeStyles((theme) => ({
  root: { marginBottom: 20, width: '100%' },
  text: {
    color: 'black!important',
    fontWeight: 'bold',
  },
  error: {
    color: `${theme.palette.error.main}!important`,
  },
  valid: {
    color: 'black!important',
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  legend: {
    '&:not(:first-child) > legend': {
      marginTop: '10px !important',
    },
    '& > legend': {
      marginBottom: 0,
    },
  },
}));

const getOptionLabel = (options) => (value) => {
  const option = options?.find((o) => String(o.value) === String(value)) || {};
  return option?.label !== 'Select...' ? option.label : '';
};

const renderValue = (field, value, productType) => {
  let suffix = '';
  let newValue = value;

  switch (field?.type) {
    case 'number':
      if (field.thousandSeparator === false) {
        break;
      }
      newValue = utils.string.t(`format.number`, { value: { number: value } });
      break;
    case 'toggle_group':
      newValue = field.options.find((option) => option.value === value)?.label;
      break;
    case 'toggle':
      newValue = utils.risk.checkBoolean(value);
      break;
    case 'checkbox':
      newValue = utils.risk.checkBoolean(value);
      break;
    case 'datepicker':
      newValue = utils.string.t(`format.date`, { value: { date: value } });
      break;
    case 'select': {
      const options = utils.generic.isValidArray(field.options, true) ? field.options : [];
      newValue = utils.generic.isValidArray(value, true) ? value.map(getOptionLabel(options)).join(', ') : getOptionLabel(options)(value);
      break;
    }
    case 'autocompletemui': {
      newValue = value?.label || '';
      break;
    }
    case 'autocompletemuiAsync': {
      if (utils.generic.isValidArray(value)) {
        newValue = '';
        for (const singleValue of value) {
          newValue += `${singleValue?.label || singleValue},`;
        }
        newValue = newValue.slice(0, -1);
      } else {
        return (
          <RiskDataAutocompleteAsyncValue
            product={productType}
            dataIndex={field.dataIndex}
            dataSource={field.dataSource}
            valueIndex={field.valueIndex}
            partialMatch={field.partialMatch}
            defaultValue={field.defaultValue}
            value={value}
          />
        );
      }
      break;
    }
    case 'array': {
      if (utils.generic.isValidArray(value)) {
        newValue = value?.map((item, index) => {
          const isLastValue = index === value.length - 1;

          const filteredItem = Object.entries(item).filter(([_, v]) => v);

          const mappedItem = filteredItem.map(([key, v], itemIndex) => {
            const def = field.arrayItemDef.find(({ name }) => name === key);
            const isLastItem = itemIndex === filteredItem.length - 1;

            return (
              <Fragment key={key}>
                {renderValue(def, v, productType)}
                {!isLastItem ? ' - ' : ''}
                {isLastItem && !isLastValue ? '; ' : ''}
              </Fragment>
            );
          });

          return mappedItem;
        });
      } else if (value) {
        newValue = Object.entries(value)
          .filter(([_, v]) => v)
          .map(([key, v]) => {
            const label = field.options?.find(({ value }) => value === key)?.label;

            return `${label}: ${v}`;
          })
          .join(';\n');
      }

      break;
    }

    case 'nested_options':
      newValue = Object.keys(value ?? {})
        .filter((key) => value[key])
        .map((key, index, array) => (
          <React.Fragment key={key}>
            {startCase(key)}: {utils.risk.getDeductibleSelectedOption(value[key]).deductible}
            {index !== array.length - 1 ? ', ' : ''}
            <br />
          </React.Fragment>
        ));
      break;
    default:
      break;
  }

  // add prefix/suffix for specific fields
  if (field?.name === 'distanceToCoast' && value) {
    suffix = ` ${utils.string.t('map.unit.miles')}`;
  }
  // the extra <span /> is used to prevent Material-UI complaining about not receiving a ReactNode
  // this happens if the value is true/false/undefined/null...
  // this workaround prevents errors in case some invalid values fall through the cracks
  return utils.generic.isValidObject(newValue) ? null : (
    <span style={{ whiteSpace: 'pre-line' }}>
      {newValue}
      {suffix}
    </span>
  );
};

const EditForm = ({ item, definitionsFields, index, formProps, setValue, getValues, productType, resetField, fieldDefinition}) => {
  const classes = useStyles();
  const visibleDefs = definitionsFields.arrayItemDef.filter((f) => f.type !== 'hidden');
  const hiddenDefs = definitionsFields.arrayItemDef.filter((f) => f.type === 'hidden');

  const [resetFields, setResetFields] = useState({});

  useEffect(() => {
    if (definitionsFields.itemTitleField && definitionsFields.itemTitle) {
      const fieldName = `${definitionsFields.name}[${index}].${definitionsFields.itemTitleField}`;
      const fieldValue = `${definitionsFields.itemTitle} ${index + 1}`;
      setValue(fieldName, fieldValue);
    }
  }, [definitionsFields.itemTitle, definitionsFields.itemTitleField, definitionsFields.name, index, setValue]);

  const objectMap = {
    address: AddRiskObjectAddress,
    object: AddRiskObject,
  };

  const serialisationClass = definitionsFields.arrayItemDef.find((def) => def.name === '@class')?.defaultValue ?? null;

  return (
    <Grid container spacing={2} data-testid="grid-container" style={{ overflowY: 'auto' }}>
      <Grid item xs={12} key={item.id} style={{ margin: '20px 30px' }}>
        {hiddenDefs.map((def) => (
          <HideFromUserWrapper key={def.name ?? 'hidden'}>
            <AddRiskFormField
              field={{
                ...def,
                name: `${definitionsFields.name}[${index}].${def.name}`,
                defaultValue: item[def.name],
                child: def.name ? def.name : null,
                index,
              }}
              formProps={formProps}
              serialisationClass={serialisationClass}
            />
          </HideFromUserWrapper>
        ))}
        <Grid container spacing={3} data-testid="grid-container">
          {visibleDefs.map((def) => {
            const { label, ...fieldProps } = def;
            const condition = utils.risk.getCondition(def, definitionsFields.arrayItemDef, fieldDefinition);
            const conditionValue = condition && formProps.watch(`${definitionsFields.name}[${index}]`);

            const isValid = !condition || (condition && utils.risk.isConditionValid(condition, conditionValue, getValues()));
            const isHidden = utils.risk.isConditionHiddenValid(def, formProps.getValues(`${definitionsFields.name}[${index}]`));
            const isDisabled = condition && !isValid;
            const hasLegend = isString(def.header);

            const name = `${definitionsFields.name}[${index}].${def.name}`;

            if (!isValid && !resetFields[name]) {
              resetField(name);
              setResetFields({ ...resetFields, [name]: true });
            } else if (isValid && resetFields[name]) {
              setResetFields({ ...resetFields, [name]: false });
            }

            if (isHidden || def.hideFromUser) {
              return (
                <HideFromUserWrapper key={def.name ?? 'hidden'}>
                  <AddRiskFormField
                    field={{
                      ...fieldProps,
                      label,
                      name: `${definitionsFields.name}[${index}].${def.name}`,
                      type: 'hidden',
                      index,
                      child: def.name,
                      defaultValue: item[def.name],
                      muiComponentProps: merge(fieldProps.muiComponentProps || {}, {
                        ...(['text', 'number'].includes(def.type) && { InputProps: { disabled: isDisabled || def.disabled } }),
                      }),
                    }}
                    formProps={formProps}
                    serialisationClass={serialisationClass}
                  />
                </HideFromUserWrapper>
              );
            }
            // grid spacer
            if (utils.risk.isGridSpacer(def)) {
              return (
                <FormGrid
                  item
                  xs={12}
                  {...def.gridSize}
                  nestedClasses={{ root: classes.spacer }}
                  data-testid={`spacer-${def.group}-${index}`}
                >
                  <span />
                </FormGrid>
              );
            }
            // column
            if (utils.risk.isArrayColumn(def)) {
              return (
                isValid && (
                  <FormGrid item xs={12} key={`${def.group}-${def.name}`}>
                    <AddRiskColumn field={{ ...def, name: `${definitionsFields.name}[${index}].${def.name}` }} formProps={formProps} />
                  </FormGrid>
                )
              );
            }
            // row
            if (utils.risk.isArrayTable(def)) {
              return (
                isValid && (
                  <Fragment key={`${def.group}-${def.name}`}>
                    {hasLegend && (
                      <FormGrid item xs={12} nestedClasses={{ root: classes.legend }}>
                        <FormLegend text={def.header} />
                      </FormGrid>
                    )}
                    <Grid item container spacing={3} xs={12} mb={5}>
                      {(() => {
                        if (['SINGLECARD', 'MULTICARD'].includes(def.display)) {
                          return (
                            <AddRiskRowMultiple
                              key={def?.name}
                              field={def}
                              definitionsFields={definitionsFields}
                              formProps={formProps}
                              formatData="PROPERTY"
                              productType={productType}
                            />
                          );
                        }
                        if (['MULTICARD OBJECT'].includes(def.display)) {
                          return (
                            <AddRiskRowMultipleObject
                              key={def?.name}
                              field={def}
                              definitionsFields={definitionsFields}
                              formProps={formProps}
                              formatData="PROPERTY"
                              productType={productType}
                            />
                          );
                        }
                        if (['VALUE_SET'].includes(def.display)) {
                          return (
                            <ValueSet
                              key={def.name}
                              field={def}
                              child={def.name}
                              prefix={`${definitionsFields.name}[${index}].`}
                              formProps={formProps}
                            />
                          );
                        }
                        return (
                          <AddRiskRow
                            field={{
                              ...def,
                              name: `${definitionsFields.name}[${index}].${def.name}`,
                              defaultValue: item[def.name],
                            }}
                            formProps={formProps}
                          />
                        );
                      })()}
                    </Grid>
                  </Fragment>
                )
              );
            }

            // object
            if (utils.risk.isObject(def)) {
              const ObjectComponent = objectMap[def.type];

              return def?.hidden ? (
                <ObjectComponent field={def} formProps={formProps} />
              ) : isValid ? (
                <FormGrid item xs={12} key={`${def.group}-${def.name}`}>
                  <ObjectComponent field={def} formProps={formProps} />
                </FormGrid>
              ) : null;
            }
            return (
              <>
                {hasLegend && (
                  <Grid item xs={12} className={classes.legend}>
                    <FormLegend text={def.header} />
                  </Grid>
                )}
                {isValid ? (
                  <Grid item xs={6} sm={6} md={4} {...(def.gridSize ?? {})} key={`visible-${item.id}`}>
                    <AddRiskFormField
                      field={{
                        ...fieldProps,
                        label,
                        name: `${definitionsFields.name}[${index}].${def.name}`,
                        defaultValue: item[def.name],
                        index,
                        child: def.name,
                        muiComponentProps: merge(fieldProps.muiComponentProps || {}, {
                          ...(['text', 'number'].includes(def.type) && { InputProps: { disabled: isDisabled || def.disabled } }),
                        }),
                      }}
                      formProps={formProps}
                      serialisationClass={serialisationClass}
                    />
                  </Grid>
                ) : null}
              </>
            );
          })}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default function RiskRowItemView({
  index,
  isBuildingEditOpen,
  definitionsFields,
  removeHandler,
  disableHandler,
  copyHandler,
  formProps,
  handleOpenEdit,
  handleCloseEdit,
  display,
  productType,
}) {
  const { risk: riskDetails } = useContext(RiskDetailsContext);
  const { fieldDefinition} = useContext(RiskContext);
  const classes = useStyles();
  const {
    formState: { errors },
    setValue,
    resetField,
    getValues
  } = useFormContext();
  const data = useWatch({
    control: formProps?.control,
    name: definitionsFields.name,
  });

  const itemTitleField = definitionsFields?.itemTitleField ?? 'buildingTitle';

  const item = data && data[index];

  const actions = [
    {
      name: 'ok',
      type: 'ok',
      color: 'secondary',
      label: utils.string.t('app.confirm'),
    },
  ];

  const visibleDefs = definitionsFields.arrayItemDef.filter(
    (f) => f.type !== 'hidden' && !f.hideFromUser && f.type !== 'label' && f.type !== 'spacer'
  );

  return (
    <Card raised elevation={2} className={classes.root}>
      <CardHeader
        action={
          <>
            {display !== 'SINGLECARD' && (
              <Tooltip title={`Duplicate ${definitionsFields?.itemTitle}`} placement="top">
                <IconButton aria-label="Duplicate" size="small" onClick={() => copyHandler(index)}>
                  <FileCopyIcon />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title={`Edit ${definitionsFields?.itemTitle}`} placement="top">
              <IconButton aria-label="Edit" size="small" onClick={handleOpenEdit}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            {riskDetails?.riskStatus !== RISK_STATUS_BOUND && item?.disableDeleteAfterBind === false ? (
              <Tooltip title={`Disable ${definitionsFields?.itemTitle}`} placement="top">
                <IconButton aria-label="Disable" size="small" onClick={() => disableHandler(index)}>
                  <BlockIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title={`Delete ${definitionsFields?.itemTitle}`} placement="top">
                <IconButton aria-label="Delete" size="small" onClick={() => removeHandler(index)}>
                  <DeleteForeverIcon />
                </IconButton>
              </Tooltip>
            )}
          </>
        }
        title={`${definitionsFields?.itemTitle} ${index + 1}`}
        subheader=""
      />
      <ModalDialog
        noScroll
        actions={actions}
        title={`Edit ${item?.[itemTitleField] ? item[itemTitleField] : ''}`}
        fullWidth
        disableBackdropClick
        enableFullScreen={false}
        maxWidth="lg"
        cancelHandler={handleCloseEdit}
        hideModal={handleCloseEdit}
        visible={isBuildingEditOpen}
      >
        <EditForm
          definitionsFields={definitionsFields}
          key={index}
          item={item}
          index={index}
          formProps={formProps}
          classes={classes}
          setValue={setValue}
          getValues={getValues}
          errors={errors}
          productType={productType}
          resetField={resetField}
          fieldDefinition={fieldDefinition}
        />
      </ModalDialog>

      <CardContent>
        {visibleDefs.map((def) => {
          const isFieldInValid =
            errors &&
            errors[definitionsFields.name] &&
            errors[definitionsFields.name][index] &&
            utils.generic.isValidObject(errors[definitionsFields.name][index], def.name);

          const condition = utils.risk.getCondition(def, definitionsFields.arrayItemDef, fieldDefinition);
          const conditionValue = condition && formProps.watch(`${definitionsFields.name}[${index}]`);

          const isValid = !condition || (condition && utils.risk.isConditionValid(condition, conditionValue, getValues()));

          return isValid ? (
            <Grid key={def.name} container spacing={1} alignItems="center">
              <Grid item xs={6} sm={6}>
                <Typography variant="body2" component="p" classes={{ root: isFieldInValid ? classes.error : classes.valid }}>
                  {def.label}:
                </Typography>
              </Grid>
              <Grid item xs={6} sm={6}>
                <Typography variant="body2" component="p" classes={{ root: classes.text }}>
                  {item && renderValue(def, item[def.name], productType)}
                </Typography>
              </Grid>
            </Grid>
          ) : null;
        })}
      </CardContent>
    </Card>
  );
}
